import { DOCUMENT } from '@angular/common';
import {
	AfterViewChecked,
	ApplicationRef,
	ChangeDetectorRef,
	Component,
	ComponentFactoryResolver,
	EmbeddedViewRef,
	HostListener,
	Inject,
	Injector,
	NgZone,
	OnDestroy,
	OnInit,
	TemplateRef,
	ViewChild,
	ViewContainerRef,
} from '@angular/core';
import { DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { of, Subject } from 'rxjs';
import { catchError, filter, take, takeUntil } from 'rxjs/operators';
import { normalizeNodeId } from '@gt/para/shared/para-node-id.util';
import { ParaProgressReportService } from '../services/para-progress-report.service';
import { ParaOrgStateService } from '../services/para-org-state.service';
import { ParaRosterService } from '../services/para-roster.service';
import { ParaScoreSubmitService } from '../services/para-score-submit.service';
import { ParaLmsAssessmentService } from './para-lms-assessment.service';
import { ParaLmsDrawerContext } from '../models/para-drawer-context.type';
import {
	resolveStudentBadgeMilestoneOverlay,
	type ParaMilestoneWithStatus,
	type ParaStudentBadgeMilestoneOverlay,
} from '../progress-table/para-milestone.util';
import {
	getEffectiveNextChallenge,
	getChallengePositionInCatalog,
	getPhaseQuestionCatalog,
	getNextChallengeQuestionAfter,
	getPreviousChallengeQuestionBefore,
	lmsStoryUrlFromNextChallenge,
	mergeProgressRowChallangeHints,
	normalizeNextChallengeForLms,
} from './lms-assessment-lms.util';
import { debugParaLms } from './para-lms-assessment.debug';
import { normalizeChipColor } from '../utils/para-color.util';
import { PARA_SCORE_RUBRIC } from '../models/para-score-rubric.const';
import { formatChallengeDisplay } from '../utils/para-challenge-display.util';
import { LmsResumeStorageOverride } from './lms-resume-storage-override.util';
import { ParaScoreStorageService } from '../services/para-score-storage.service';
import { ParaSelectedAssessmentService } from '../services/para-selected-assessment.service';
import { ParaAssessmentSeriesService } from '../services/para-assessment-series.service';
import {
	ParaAssessmentContextService,
	type ParaAssessmentContextDto,
	type ParaLmsHeaderSupplementDto,
} from '../services/para-assessment-context.service';
import {
	buildLmsActiveSlideGuideLines,
	filterLmsHeaderSupplementsForActiveSlide,
	isFirstSlideOfLmsChallenge,
	normalizeLmsHeaderId,
	parseScoringGuideLineSegments,
	resolveLmsHeaderSupplementsForChallenge,
	type ParaScoreButton,
	type StoryScoreButtonProbe,
} from './lms-header-supplement.util';
import {
	canStartParaLmsAssessment,
	getParaLmsStartBlockReason,
	isParaScreenerStyleAssessment,
	isParaTransformStyleAssessment,
} from './para-lms-assessment-start.util';
import { LmsStoryIframeBridge, type LmsStoryLayoutSnapshot } from './lms-story-iframe-bridge.service';

/** Minimal Navigation API surface for reload intercept (see `setupPageReloadGuard`). */
interface ParaNavigationReloadEvent extends Event {
	canIntercept?: boolean;
	navigationType?: string;
	intercept?: (options: { handler: () => void | Promise<void> }) => void;
}

interface ParaWindowNavigation {
	addEventListener(type: 'navigate', listener: (event: Event) => void): void;
	removeEventListener(type: 'navigate', listener: (event: Event) => void): void;
}

@Component({
	selector: 'app-para-lms-assessment',
	templateUrl: './lms-assessment-slideout.component.html',
	styleUrls: ['./lms-assessment-slideout.component.css'],
})
export class LmsAssessmentSlideoutComponent implements OnInit, OnDestroy, AfterViewChecked {
	private readonly destroy$ = new Subject<void>();
	@ViewChild('scoreReviewPortal') private scoreReviewPortal?: TemplateRef<unknown>;
	private scoreReviewPortalOutlet: DomPortalOutlet | null = null;
	private scoreReviewPortalRef: EmbeddedViewRef<unknown> | null = null;
	private scoreReviewPortalVisible = false;
	/**
	 * Storyline resume/suspend data uses `localStorage`. For a **same-origin** LMS URL (e.g.
	 * `/assets/LMS/...`), the iframe shares `localStorage` with the parent window — so we can
	 * block resume reads/writes from the parent frame without regenerating published `story.html`.
	 *
	 * **Cross-origin** LMS (different scheme/host/port) has its own storage; this override does
	 * not apply there.
	 */
	private readonly resumeStorageOverride = new LmsResumeStorageOverride({
		isResumeLikeValue: (value) => this.isStorylineResumeLikeValue(value),
		debug: (...args: any[]) => (debugParaLms as any)(...args),
	});

	public drawerState: 'closed' | 'assessing' | 'confirmation' | 'submitting' | 'done' | 'confirmClose' = 'closed';

	/** Why the discard-unsaved-scores modal is shown (close drawer vs page refresh). */
	private confirmCloseIntent: 'drawer' | 'page-refresh' | null = null;

	/** Navigation API reload intercept — resolve to allow refresh, reject to cancel. */
	private reloadNavigationFinish: (() => void) | null = null;
	private reloadNavigationCancel: (() => void) | null = null;
	private pageReloadNavigateListener: ((event: Event) => void) | null = null;

	activeStudent: any;
	students: any[] = [];
	questions: any[] = [];
	private assessmentId: string | null = null;
	/** UI context that should survive roster swaps (badge colors, hints, etc.). */
	public lmsContext: ParaLmsDrawerContext | null = null;

	iframeUrl: SafeResourceUrl | null = null;
	/** Forces iframe reload when the LMS path matches the previous load (Angular may skip same URL). */
	private lastStoryUrlPath: string | null = null;
	confirmScore: number;
	isLoadingIframe = false;
	isSubmitting = false;
	isLocked = false;
	lockReason: string | null = null;
	private assessmentSeriesBlocked = false;
	private activeAssessmentSlug(): string | null {
		const raw = this.assessmentId ?? this.currentAssessmentId();
		const s = raw != null ? String(raw).trim() : '';
		return s ? s : null;
	}

	isScreenerLmsAssessment(): boolean {
		return isParaScreenerStyleAssessment(this.activeAssessmentSlug());
	}

	isTransformLmsAssessment(): boolean {
		return isParaTransformStyleAssessment(this.activeAssessmentSlug());
	}

	/** Transform: submit pending scores from the drawer header. */
	showLmsHeaderSubmit(): boolean {
		return !this.isLocked && this.isTransformLmsAssessment() && this.scoreStorageService.count > 0 && this.drawerState === 'assessing';
	}

	selectedDate: string;
	showDateSelector: boolean;
	drawerEntered = false;

	public scoreRubric = PARA_SCORE_RUBRIC;

	private closeDrawerTimer: ReturnType<typeof setTimeout> | undefined;
	/** Node id used to load the roster when the drawer opened (progress table override). Submit/refresh must match this, not only {@link ParaOrgStateService.getSelectedClassroomSnapshot}. */
	private assessmentContextNodeId: number | null = null;

	/** Cached `getAssessmentContext` for LMS UI (e.g. Save & Done stop rules). */
	private lmsAssessmentContext: ParaAssessmentContextDto | null = null;

	/**
	 * Storyline slide key for scorer copy (`Para_Lms_Header_Supplement.lmsHeaderId`).
	 * Set from published story via `parent.paraLmsSetActiveHeader('slide2')` on slide enter.
	 */
	private activeLmsHeaderId: string | null = null;

	/** Visible Storyline score buttons (1–4), refreshed from the iframe DOM. */
	private storyVisibleScoreButtons: ParaScoreButton[] = [];
	/** Layout probes for visible score buttons (position + optional IO ratio). */
	private storyScoreButtonProbes: StoryScoreButtonProbe[] = [];
	private storyIframeViewWidth = 0;
	/** Whether the in-slide NEXT control is currently visible in the iframe. */
	private storyNextControlVisible = false;

	/** User toggle for the active-slide scoring guide overlay. */
	lmsSlideGuideVisible = false;
	lmsSlideGuidePanelStyle: Record<string, string> = {};
	private lmsStableSlideGuideLines: string[] = [];
	private lmsSlideGuideResizeTimer: ReturnType<typeof setTimeout> | undefined;
	private lastLmsSlideGuideKey: string | null = null;

	private computeLmsActiveSlideGuideLines(): string[] {
		if (!this.drawerOpen || this.drawerState !== 'assessing') {
			return [];
		}
		return buildLmsActiveSlideGuideLines({
			supplements: this.lmsHeaderSupplements,
			activeLmsHeaderId: this.activeLmsHeaderId,
			scoreButtonProbes: this.storyScoreButtonProbes,
			viewWidth: this.storyIframeViewWidth,
			visibleScoreButtons: this.storyVisibleScoreButtons,
			nextControlVisible: this.storyNextControlVisible,
		});
	}

	private refreshLmsStableSlideGuideLines(): void {
		const lines = this.computeLmsActiveSlideGuideLines();
		this.lmsStableSlideGuideLines = lines;
	}

	/** Active slide scoring guide lines (from Word doc), shown when story controls are visible. */
	get lmsActiveSlideGuideLines(): string[] {
		if (!this.drawerOpen || this.drawerState !== 'assessing') {
			return [];
		}
		return this.lmsStableSlideGuideLines;
	}

	lmsSlideGuideLineSegments(line: string) {
		return parseScoringGuideLineSegments(line);
	}

	toggleLmsSlideGuide(): void {
		if (this.lmsSlideGuideVisible) {
			this.lmsSlideGuideVisible = false;
			this.lmsSlideGuidePanelStyle = {};
			return;
		}
		this.updateLmsSlideGuidePanelPosition();
		this.lmsSlideGuideVisible = true;
		this.cdr.detectChanges();
	}

	private updateLmsSlideGuidePanelPosition(): void {
		const btn = this.document.querySelector('[data-testid="para-lms-slide-guide-show"]') as HTMLElement | null;
		if (!btn) {
			return;
		}
		// Fixed descendants are positioned against the drawer, not the viewport (drawer uses transform).
		const drawer = btn.closest('.drawer') as HTMLElement | null;
		if (!drawer) {
			return;
		}
		const btnRect = btn.getBoundingClientRect();
		const drawerRect = drawer.getBoundingClientRect();
		const width = Math.min(352, drawerRect.width - 16);
		const btnCenterX = btnRect.left + btnRect.width / 2;
		let left = btnCenterX - drawerRect.left - width / 2;
		const minLeft = 8;
		const maxLeft = drawerRect.width - width - 8;
		left = Math.max(minLeft, Math.min(left, maxLeft));
		this.lmsSlideGuidePanelStyle = {
			top: `${btnRect.bottom - drawerRect.top + 6}px`,
			left: `${left}px`,
			width: `${width}px`,
		};
		this.cdr.markForCheck();
	}

	private syncLmsSlideGuideVisibilityForActiveSlide(): void {
		this.refreshLmsStableSlideGuideLines();
		const lines = this.computeLmsActiveSlideGuideLines();
		if (!lines.length) {
			this.lastLmsSlideGuideKey = null;
			this.lmsSlideGuideVisible = false;
			return;
		}
		const key = lines.join('\n');
		if (key !== this.lastLmsSlideGuideKey) {
			this.lastLmsSlideGuideKey = key;
			this.lmsSlideGuideVisible = false;
		}
	}

	constructor(
		private paraProgressReport: ParaProgressReportService,
		private paraOrgState: ParaOrgStateService,
		private paraRoster: ParaRosterService,
		private paraScoreSubmit: ParaScoreSubmitService,
		private sanitizer: DomSanitizer,
		private ngZone: NgZone,
		public scoreStorageService: ParaScoreStorageService,
		private paraLmsService: ParaLmsAssessmentService,
		private readonly paraSelectedAssessment: ParaSelectedAssessmentService,
		private readonly paraSeries: ParaAssessmentSeriesService,
		private readonly paraAssessmentContext: ParaAssessmentContextService,
		private cdr: ChangeDetectorRef,
		private vcr: ViewContainerRef,
		private appRef: ApplicationRef,
		private injector: Injector,
		private componentFactoryResolver: ComponentFactoryResolver,
		private readonly storyBridge: LmsStoryIframeBridge,
		@Inject(DOCUMENT) private document: Document
	) {}

	private currentAssessmentId(): string | null {
		// Selected assessment drives the roster questions + LMS traversal.
		// Falls back to whatever the roster response returns.
		const raw = (this.paraSelectedAssessment as any)?.selectedAssessmentIdSubject?.value ?? null;
		const s = raw != null ? String(raw).trim() : '';
		return s ? s : null;
	}

	get drawerOpen(): boolean {
		return this.drawerState !== 'closed';
	}

	get showCloseConfirm(): boolean {
		return this.drawerState === 'confirmClose';
	}

	/** Body copy verb for the discard modal ("Closing" vs "Refreshing"). */
	get confirmDiscardActionLabel(): string {
		return this.confirmCloseIntent === 'page-refresh' ? 'Refreshing' : 'Closing';
	}

	/** Primary discard button label for the discard modal. */
	get confirmDiscardButtonLabel(): string {
		return this.confirmCloseIntent === 'page-refresh' ? 'Discard & Refresh' : 'Discard & Close';
	}

	/** Back-compat for tests / callers expecting `isOpen`. */
	get isOpen(): boolean {
		return this.drawerOpen;
	}

	ngOnInit(): void {
		const today = new Date();
		this.selectedDate = today.toISOString().split('T')[0];

		this.paraSelectedAssessment.activeSeriesItem$.pipe(takeUntil(this.destroy$)).subscribe((item) => {
			this.isLocked = item?.isLocked ?? false;
			this.lockReason = item?.lockReason ?? null;
			this.assessmentSeriesBlocked = !!(item?.isLocked || item?.isReadOnly);
			this.cdr.markForCheck();
		});

		this.paraLmsService.open$
			.pipe(
				filter((s) => s != null),
				takeUntil(this.destroy$)
			)
			.subscribe((payload: any) => {
				// New shape: { partial, context }. Back-compat: payload itself can be the partial.
				const partial = payload?.partial != null ? payload.partial : payload;
				const ctx: ParaLmsDrawerContext | null = payload?.context ?? null;
				const nodeId = ctx?.nodeId ?? null;
				this.lmsContext = ctx;
				debugParaLms('open$ received', { partial, nodeId, ctx, rawPayload: payload });
				this.resolveAndOpen(partial, nodeId);
			});

		this.setupPageReloadGuard();
	}

	ngAfterViewChecked(): void {
		this.syncScoreReviewPortal();
	}

	ngOnDestroy(): void {
		if (this.closeDrawerTimer != null) {
			clearTimeout(this.closeDrawerTimer);
		}
		this.detachScoreReviewPortal();
		this.teardownPageReloadGuard();
		this.destroy$.next();
		this.destroy$.complete();
		this.closeDrawerImmediate();
	}

	private syncScoreReviewPortal(): void {
		const shouldShow = this.drawerOpen && this.drawerState === 'confirmation';
		if (shouldShow === this.scoreReviewPortalVisible) {
			return;
		}
		this.scoreReviewPortalVisible = shouldShow;
		if (shouldShow) {
			this.attachScoreReviewPortal();
			this.document.body.classList.add('para-lms-score-review-open');
			return;
		}
		this.detachScoreReviewPortal();
		this.document.body.classList.remove('para-lms-score-review-open');
	}

	private attachScoreReviewPortal(): void {
		if (this.scoreReviewPortalRef || !this.scoreReviewPortal) {
			return;
		}
		const portal = new TemplatePortal(this.scoreReviewPortal, this.vcr);
		this.scoreReviewPortalOutlet = new DomPortalOutlet(
			this.document.body,
			this.componentFactoryResolver,
			this.appRef,
			this.injector
		);
		this.scoreReviewPortalRef = this.scoreReviewPortalOutlet.attach(portal) as EmbeddedViewRef<unknown>;
	}

	private detachScoreReviewPortal(): void {
		if (this.scoreReviewPortalOutlet?.hasAttached()) {
			this.scoreReviewPortalOutlet.detach();
		}
		this.scoreReviewPortalOutlet = null;
		this.scoreReviewPortalRef = null;
		this.document.body.classList.remove('para-lms-score-review-open');
	}

	@HostListener('document:keydown.escape')
	onEscKey(): void {
		if (this.drawerOpen) {
			this.closeDrawer();
		}
	}

	/**
	 * Tab close / navigate away when Navigation API cannot intercept (e.g. closing the tab).
	 * Toolbar refresh uses {@link setupPageReloadGuard} so the same custom modal can appear.
	 */
	@HostListener('window:beforeunload', ['$event'])
	onBeforeUnload(event: BeforeUnloadEvent): void {
		if (!this.shouldGuardUnsavedScores() || this.reloadNavigationFinish != null) {
			return;
		}
		event.preventDefault();
		event.returnValue = '';
	}

	/** F5 / Ctrl+R / Cmd+R — show the same discard modal as closing the drawer. */
	@HostListener('document:keydown', ['$event'])
	onDocumentKeydown(event: KeyboardEvent): void {
		if (!this.shouldGuardUnsavedScores()) {
			return;
		}
		const isRefresh = event.key === 'F5' || ((event.ctrlKey || event.metaKey) && (event.key === 'r' || event.key === 'R'));
		if (!isRefresh) {
			return;
		}
		event.preventDefault();
		this.promptUnsavedScoresDiscard('page-refresh');
	}

	private shouldGuardUnsavedScores(): boolean {
		return (
			this.drawerOpen &&
			this.scoreStorageService.count > 0 &&
			this.drawerState !== 'confirmClose' &&
			this.drawerState !== 'submitting' &&
			this.drawerState !== 'done'
		);
	}

	private promptUnsavedScoresDiscard(intent: 'drawer' | 'page-refresh'): void {
		if (!this.shouldGuardUnsavedScores()) {
			return;
		}
		this.confirmCloseIntent = intent;
		this.drawerState = 'confirmClose';
		this.cdr.markForCheck();
	}

	/**
	 * Intercepts browser-toolbar reload so we can show the same discard modal as closing the drawer.
	 * Falls back to keyboard intercept + `beforeunload` when `window.navigation` is unavailable.
	 */
	private setupPageReloadGuard(): void {
		const navigation = (window as Window & { navigation?: ParaWindowNavigation }).navigation;
		if (!navigation?.addEventListener) {
			return;
		}

		this.pageReloadNavigateListener = (raw: Event) => {
			const event = raw as ParaNavigationReloadEvent;
			if (!event.canIntercept || event.navigationType !== 'reload' || !this.shouldGuardUnsavedScores()) {
				return;
			}
			event.intercept?.({
				handler: () => this.waitForRefreshDiscardConfirmation(),
			});
		};
		navigation.addEventListener('navigate', this.pageReloadNavigateListener);
	}

	private teardownPageReloadGuard(): void {
		const navigation = (window as Window & { navigation?: ParaWindowNavigation }).navigation;
		if (navigation && this.pageReloadNavigateListener) {
			navigation.removeEventListener('navigate', this.pageReloadNavigateListener);
		}
		this.pageReloadNavigateListener = null;
		this.clearPendingReloadNavigation();
	}

	private clearPendingReloadNavigation(): void {
		this.reloadNavigationFinish = null;
		this.reloadNavigationCancel = null;
	}

	private waitForRefreshDiscardConfirmation(): Promise<void> {
		return new Promise((resolve, reject) => {
			this.reloadNavigationFinish = resolve;
			this.reloadNavigationCancel = () => reject(new DOMException('Navigation aborted', 'AbortError'));
			this.ngZone.run(() => this.promptUnsavedScoresDiscard('page-refresh'));
		});
	}

	@HostListener('window:resize')
	onWindowResize(): void {
		this.storyBridge.handleWindowResize();
		if (!this.lmsSlideGuideVisible) {
			return;
		}
		if (this.lmsSlideGuideResizeTimer != null) {
			clearTimeout(this.lmsSlideGuideResizeTimer);
		}
		this.lmsSlideGuideResizeTimer = setTimeout(() => {
			this.lmsSlideGuideResizeTimer = undefined;
			this.updateLmsSlideGuidePanelPosition();
		}, 100);
	}

	getCurrentChallenge(): any {
		return this.activeStudent?.challange?.nextChallenge;
	}

	/** Template helper: "challenged" = we have a current/next challenge object to score. */
	isChallenged(): boolean {
		const c = this.getCurrentChallenge();
		return c != null && typeof c === 'object';
	}

	/**
	 * 1-based challenge number used for LMS assets and UI.
	 *
	 * PARA LMS uses `questionNumber` (already 1-based folder number).
	 */
	getCurrentChallengeNumber(): number | null {
		const c = this.getCurrentChallenge();
		if (c == null || typeof c !== 'object') return null;

		// Preferred: already 1-based challenge folder number (used by LMS assets).
		const qn = Number((c as any).questionNumber);
		if (Number.isFinite(qn) && qn > 0) return Math.floor(qn);

		return null;
	}

	/**
	 * "Save & Done" visibility: requires `lms.saveDone` in assessment context `stopRules`
	 * and current challenge >= `minInclusiveChallenge` (default 2).
	 */
	showSaveAndDoneFromAssessmentContext(): boolean {
		if (!this.isChallenged()) return false;
		const rules = this.lmsAssessmentContext?.stopRules;
		if (!Array.isArray(rules) || rules.length === 0) return false;
		const rule = rules.find((r) => r && r.ruleId === 'lms.saveDone');
		if (!rule) return false;
		const raw = rule.params != null ? (rule.params as Record<string, unknown>)['minInclusiveChallenge'] : undefined;
		let minInclusive = 2;
		if (raw !== undefined && raw !== null) {
			const n = Number(raw);
			if (Number.isFinite(n) && n > 0) minInclusive = Math.floor(n);
		}
		const cur = this.getCurrentChallengeNumber();
		if (cur == null) return false;
		return cur >= minInclusive;
	}

	/**
	 * Hide "Save & Next" when the catalog has no following question in this student's phase
	 * (same resolution as {@link storeScore}).
	 */
	hasNextCatalogChallengeAfterCurrent(): boolean {
		if (!this.isChallenged() || !this.activeStudent) {
			return false;
		}
		const rawQ = this.getCurrentChallenge();
		const q = normalizeNextChallengeForLms(rawQ, this.questions) ?? rawQ;
		return (
			getNextChallengeQuestionAfter(q, this.questions, this.activeStudent.assessmentBookId, this.activeStudent.assessmentTemplateId) != null
		);
	}

	hasPreviousCatalogChallengeBeforeCurrent(): boolean {
		if (!this.isChallenged() || !this.activeStudent) {
			return false;
		}
		const rawQ = this.getCurrentChallenge();
		const q = normalizeNextChallengeForLms(rawQ, this.questions) ?? rawQ;
		return (
			getPreviousChallengeQuestionBefore(
				q,
				this.questions,
				this.activeStudent.assessmentBookId,
				this.activeStudent.assessmentTemplateId
			) != null
		);
	}

	canGoToPreviousChallenge(): boolean {
		return this.drawerState === 'assessing' && !this.isLocked && this.hasPreviousCatalogChallengeBeforeCurrent();
	}

	/** True when Storyline reports slide 1 (or before the first slide enter callback). */
	isOnFirstSlideOfChallenge(): boolean {
		return isFirstSlideOfLmsChallenge({
			activeLmsHeaderId: this.activeLmsHeaderId,
			scoreButtonProbes: this.storyScoreButtonProbes,
			viewWidth: this.storyIframeViewWidth,
			supplements: this.lmsHeaderSupplements,
			visibleScoreButtons: this.storyVisibleScoreButtons,
		});
	}

	goToPreviousChallenge(): void {
		this.logPrevCrossChallengeDebug('goToPreviousChallenge called');
		if (!this.canGoToPreviousChallenge() || !this.activeStudent?.challange) {
			this.logPrevCrossChallengeDebug('goToPreviousChallenge aborted — not allowed or no active challenge', {
				hasActiveChallenge: !!this.activeStudent?.challange,
			});
			return;
		}
		this.resumeStorageOverride.prepareForFreshChallengeLoad();
		const rawQ = this.getCurrentChallenge();
		const q = normalizeNextChallengeForLms(rawQ, this.questions) ?? rawQ;
		const prevQuestion = getPreviousChallengeQuestionBefore(
			q,
			this.questions,
			this.activeStudent.assessmentBookId,
			this.activeStudent.assessmentTemplateId
		);
		if (!prevQuestion) {
			this.logPrevCrossChallengeDebug('goToPreviousChallenge aborted — no previous question in catalog');
			return;
		}
		this.logPrevCrossChallengeDebug('goToPreviousChallenge loading previous question', {
			prevQuestionId: prevQuestion.id,
			prevQuestionNumber: prevQuestion.questionNumber,
			prevLabel: prevQuestion.label,
		});
		this.activeStudent.challange.nextChallenge = prevQuestion;
		this.drawerState = 'assessing';
		this.loadStoryIframe(this.activeStudent);
		this.cdr.markForCheck();
	}

	navigateToChallengeForRescore(storageKey: string): void {
		if (this.isLocked) {
			return;
		}
		const stored = this.scoreStorageService.storedScores[String(storageKey)];
		const question = stored?.question;
		if (!question || !this.activeStudent?.challange) {
			return;
		}
		this.activeStudent.challange.nextChallenge = normalizeNextChallengeForLms(question, this.questions) ?? question;
		this.drawerState = 'assessing';
		this.loadStoryIframe(this.activeStudent);
		this.cdr.markForCheck();
	}

	getCurrentChallengeDisplay(): string {
		return formatChallengeDisplay(this.getCurrentChallengeNumber());
	}

	getCurrentChallengeLabelClass(): string {
		const q = this.getCurrentChallenge();
		const testId = q?.testId;
		return testId != null ? `labelcolor-${testId}` : 'tw-bg-slate-100 tw-text-para-primary tw-border tw-border-slate-200';
	}

	getHighestChallengePhaseLabel(): string | null {
		// API shapes vary: some responses nest under `challange.highestChallenge`,
		// others may include `highestChallenge` at the root.
		return (
			(this.lmsContext?.highestChallenge as any)?.phase?.shortLabel ??
			this.activeStudent?.highestChallenge?.phase?.shortLabel ??
			this.activeStudent?.challange?.highestChallenge?.phase?.shortLabel ??
			null
		);
	}

	getHighestChallengePhaseColor(): string | null {
		const raw =
			(this.lmsContext?.highestChallenge as any)?.phase?.color ??
			this.activeStudent?.highestChallenge?.phase?.color ??
			this.activeStudent?.challange?.highestChallenge?.phase?.color ??
			null;
		if (raw == null || String(raw).trim() === '') return null;
		const s = String(raw).trim();
		return s.startsWith('#') ? s : `#${s}`;
	}

	getHighestChallengeDisplay(): string {
		const n = Number(
			(this.lmsContext?.highestChallenge as any)?.challengeId ??
				this.activeStudent?.highestChallenge?.challengeId ??
				this.activeStudent?.challange?.highestChallenge?.challengeId
		);
		if (!Number.isFinite(n) || n < 0) return '0';
		return String(Math.floor(n)).padStart(2, '0');
	}

	/** Milestones for drawer header (from progress table context; mirrors student detail). */
	get lmsMilestonesWithStatus(): ParaMilestoneWithStatus[] {
		const list = this.lmsContext?.milestonesWithStatus;
		return Array.isArray(list) ? list : [];
	}

	get lmsStudentBadgeMilestoneOverlay(): ParaStudentBadgeMilestoneOverlay | null {
		return resolveStudentBadgeMilestoneOverlay(this.lmsMilestonesWithStatus);
	}

	/** Supplementary scorer copy for the challenge currently loaded in the LMS story. */
	get lmsHeaderSupplements(): ParaLmsHeaderSupplementDto[] {
		const rawQ = this.getCurrentChallenge();
		const q = normalizeNextChallengeForLms(rawQ, this.questions) ?? rawQ;
		return resolveLmsHeaderSupplementsForChallenge(q, this.questions, this.lmsAssessmentContext?.lmsHeaderSupplements);
	}

	get lmsActiveSlideSupplements(): ParaLmsHeaderSupplementDto[] {
		return filterLmsHeaderSupplementsForActiveSlide(this.lmsHeaderSupplements, this.activeLmsHeaderId);
	}

	/** Challenge title for the drawer header (prefer named label over bare challenge number). */
	get lmsCurrentChallengeLabel(): string | null {
		const rawQ = this.getCurrentChallenge();
		const q = normalizeNextChallengeForLms(rawQ, this.questions) ?? rawQ;
		if (!q || typeof q !== 'object') {
			return null;
		}
		const label = String((q as { label?: string }).label ?? '').trim();
		const question = String((q as { question?: string }).question ?? '').trim();
		const num = this.getCurrentChallengeNumber();
		const numStr = num != null ? String(num) : '';
		if (label && label !== numStr) {
			return label;
		}
		return question || null;
	}

	/** Current challenge index within the phase catalog (e.g. `2 / 15`). */
	get lmsChallengeNavLabel(): string | null {
		if (!this.isChallenged() || !this.activeStudent) {
			return null;
		}
		const rawQ = this.getCurrentChallenge();
		const q = normalizeNextChallengeForLms(rawQ, this.questions) ?? rawQ;
		const pos = getChallengePositionInCatalog(q, this.questions, this.activeStudent);
		if (!pos) {
			const n = this.getCurrentChallengeNumber();
			const total = getPhaseQuestionCatalog(
				this.questions,
				this.activeStudent.assessmentBookId,
				this.activeStudent.assessmentTemplateId,
				q
			).length;
			if (n != null && total > 0) {
				return `${n} / ${total}`;
			}
			return null;
		}
		return `${pos.position} / ${pos.total}`;
	}

	get lmsDrawerStudentName(): string {
		const name = this.lmsContext?.drawerHeader?.name?.trim();
		if (name) return name;
		const f = (this.activeStudent?.student?.firstName ?? '').toString().trim();
		const l = (this.activeStudent?.student?.lastName ?? '').toString().trim();
		return `${f} ${l}`.trim() || 'Student';
	}

	get lmsDrawerStudentGrade(): string {
		const g = this.lmsContext?.drawerHeader?.grade;
		if (g != null && String(g).trim() !== '') return String(g).trim();
		const alt = this.activeStudent?.grade ?? this.activeStudent?.student?.grade;
		return alt != null ? String(alt).trim() : '';
	}

	get lmsDrawerBadgePhase(): string | number | null {
		const h = this.lmsContext?.drawerHeader;
		if (h) return h.badgePhase ?? null;
		return this.getHighestChallengePhaseLabel();
	}

	get lmsDrawerBadgeChallenge(): string | number {
		const h = this.lmsContext?.drawerHeader;
		if (h) return h.badgeChallenge ?? '—';
		return this.getHighestChallengeDisplay();
	}

	get lmsDrawerBadgeBg(): string | null {
		return (
			this.lmsContext?.drawerHeader?.badgeColors?.bg ??
			this.lmsContext?.badgeColors?.bg ??
			(this.activeStudent as any)?.__paraBadge?.bg ??
			null
		);
	}

	get lmsDrawerBadgeFg(): string | null {
		return (
			this.lmsContext?.drawerHeader?.badgeColors?.fg ??
			this.lmsContext?.badgeColors?.fg ??
			(this.activeStudent as any)?.__paraBadge?.fg ??
			null
		);
	}

	get lmsDrawerBadgeBorder(): string | null {
		return (
			this.lmsContext?.drawerHeader?.badgeColors?.border ??
			this.lmsContext?.badgeColors?.border ??
			(this.activeStudent as any)?.__paraBadge?.border ??
			null
		);
	}

	/** Same precedence as student detail row phase color, with LMS hex normalization as fallback. */
	getLmsPhaseChipColor(): string | null {
		const explicit = this.lmsContext?.phaseChipColor;
		const normalized = normalizeChipColor(explicit);
		if (normalized) return normalized;
		return this.getHighestChallengePhaseColor();
	}

	private loadLmsAssessmentContext(): void {
		const nid = this.assessmentContextNodeId;
		if (nid == null) {
			return;
		}
		this.lmsAssessmentContext = null;
		this.paraAssessmentContext
			.getAssessmentContext(nid, this.assessmentId)
			.pipe(
				take(1),
				takeUntil(this.destroy$),
				catchError(() => of(null))
			)
			.subscribe((ctx) => {
				this.lmsAssessmentContext = ctx;
				this.cdr.markForCheck();
			});
	}

	private resolveAndOpen(partial: any, nodeIdOverride?: unknown): void {
		if (this.drawerOpen) {
			this.closeDrawerImmediate();
		}
		const classroomId = normalizeNodeId(nodeIdOverride) ?? normalizeNodeId(this.paraOrgState.getSelectedClassroomSnapshot()?.id);
		debugParaLms('resolveAndOpen', {
			nodeIdOverride,
			paraSelectedClassroomId: this.paraOrgState.getSelectedClassroomSnapshot()?.id,
			classroomId,
			partialStudentId: partial?.id,
		});
		if (classroomId == null) {
			debugParaLms('abort: no classroomId');
			console.warn('[PARA LMS] No classroom selected; drawer will not open. Wait for dashboard load or select a classroom.');
			return;
		}
		if (partial?.id == null) {
			debugParaLms('abort: no partial.id');
			console.warn('[PARA LMS] Could not identify the student (missing id); drawer will not open.');
			return;
		}
		this.paraRoster
			.getStudents$(classroomId, this.currentAssessmentId())
			.pipe(take(1))
			.subscribe({
				next: (data: any) => {
					this.assessmentId = data?.assessment?.assessmentId ? String(data.assessment.assessmentId) : null;
					const rosterStudents = Array.isArray(data?.students) ? data.students : [];
					const fromRoster = Array.isArray(data?.questions) ? data.questions : [];
					const questions = fromRoster;
					this.assessmentContextNodeId = classroomId;
					this.questions = questions;
					const assessableStudents = this.filterAssessableRosterStudents(rosterStudents);
					this.students = assessableStudents;
					const full = assessableStudents.find((s: any) => String(s.id) === String(partial.id));
					debugParaLms('getStudents$ roster', {
						rosterStudentCount: rosterStudents.length,
						assessableStudentCount: assessableStudents.length,
						rosterQuestionCount: fromRoster.length,
						foundStudent: !!full,
						rawChallangeBeforeEffective: full ? { ...full.challange } : null,
					});
					if (!full) {
						const clicked = rosterStudents.find((s: any) => String(s.id) === String(partial.id));
						this.prepareStudentForLms(clicked ?? partial, partial);
						const blockReason = getParaLmsStartBlockReason(clicked ?? partial, {
							seriesBlocked: this.assessmentSeriesBlocked,
							catalogChallengeCount: questions.length,
						});
						debugParaLms('abort: student not assessable in LMS roster', {
							lookedForId: partial.id,
							blockReason,
							assessableCount: assessableStudents.length,
						});
						console.warn(
							'[PARA LMS] Student cannot be assessed in the LMS drawer (exempt, finished, or locked).',
							blockReason ?? { id: partial.id }
						);
						return;
					}
					this.loadLmsAssessmentContext();

					this.prepareStudentForLms(full, partial);
					debugParaLms('resolved story path (before drawer)', {
						storyPath: lmsStoryUrlFromNextChallenge(full?.challange?.nextChallenge, this.assessmentId),
						nextChallengeUsed: full?.challange?.nextChallenge,
						assessableRosterSize: assessableStudents.length,
					});
					this.openDrawerWithStudent(full);
				},
				error: (err: unknown) => {
					debugParaLms('getStudents$ error', err);
					console.error('[PARA LMS] Could not load students for this class.', err);
				},
			});
	}

	/**
	 * Roster slice for the LMS drawer: non-exempt students with a remaining challenge (not finished).
	 */
	private filterAssessableRosterStudents(rosterStudents: any[]): any[] {
		const opts = {
			seriesBlocked: this.assessmentSeriesBlocked,
			catalogChallengeCount: Array.isArray(this.questions) ? this.questions.length : 0,
		};
		const out: any[] = [];
		for (const raw of rosterStudents) {
			if (!raw) {
				continue;
			}
			const row = {
				...raw,
				student: raw.student ? { ...raw.student } : raw.student,
				challange: raw.challange ? { ...raw.challange } : raw.challange,
			};
			this.prepareStudentForLms(row, null);
			if (canStartParaLmsAssessment(row, opts)) {
				out.push(row);
			}
		}
		return out;
	}

	/** Merge progress-row hints and resolve nextChallenge for LMS iframe + scoring. */
	private prepareStudentForLms(full: any, partial: any | null): void {
		if (!full) {
			return;
		}
		mergeProgressRowChallangeHints(full, partial);
		const badgeFromHeader = this.lmsContext?.drawerHeader?.badgeColors ?? null;
		const badgeFromContext = this.lmsContext?.badgeColors ?? badgeFromHeader ?? null;
		const badgeFromPartial = partial?.__paraBadge ?? null;
		const badge = badgeFromContext ?? badgeFromPartial;
		if (badge != null) {
			(full as any).__paraBadge = badge;
		}
		full.challange = full.challange || {};
		const hasApiLmsChallenges = Object.prototype.hasOwnProperty.call(full, 'nextChallenge');
		if (hasApiLmsChallenges) {
			full.challange.nextChallenge = full.nextChallenge ?? undefined;
		} else {
			const effective = getEffectiveNextChallenge(full, this.questions);
			if (effective) {
				full.challange.nextChallenge = effective;
			}
		}
		const nc = full.challange?.nextChallenge;
		if (nc && typeof nc === 'object') {
			full.challange.nextChallenge = normalizeNextChallengeForLms(nc, this.questions);
		}
	}

	private openDrawerWithStudent(student: any): void {
		this.storyBridge.detach();
		this.activeLmsHeaderId = null;
		this.storyVisibleScoreButtons = [];
		this.storyNextControlVisible = false;
		this.activeStudent = student;

		this.drawerState = 'assessing';
		this.drawerEntered = false;
		this.loadStoryIframe(student);
		this.cdr.detectChanges();
		// Use a macrotask to ensure the drawer DOM is in place before we toggle the open class.
		// This is more reliable than nested rAF when other overlays just unmounted.
		setTimeout(() => {
			this.drawerEntered = true;
			this.cdr.markForCheck();
		}, 0);
	}

	private loadStoryIframe(student: any): void {
		this.resumeStorageOverride.prepareForFreshChallengeLoad();
		this.storyBridge.detach();
		this.activeLmsHeaderId = null;
		this.storyVisibleScoreButtons = [];
		this.storyNextControlVisible = false;
		this.clearWindowStoryHooks();
		const nc = student?.challange?.nextChallenge;
		const urlPath = lmsStoryUrlFromNextChallenge(nc, this.assessmentId);
		debugParaLms('loadStoryIframe', {
			urlPath,
			nextChallenge: nc,
			sourceFileHint: urlPath != null ? String(urlPath).replace(/^\//, 'src/') : null,
		});
		if (!urlPath) {
			this.lastStoryUrlPath = null;
			this.iframeUrl = null;
			console.warn("[PARA LMS] No LMS story available for this student's current challenge (missing or completed).");
			this.cdr.detectChanges();
			return;
		}
		const assignSrc = () => {
			this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(urlPath);
			this.lastStoryUrlPath = urlPath;
			this.setWindowStoryHooks();
			this.cdr.detectChanges();
		};
		if (urlPath === this.lastStoryUrlPath) {
			this.iframeUrl = null;
			this.cdr.detectChanges();
			requestAnimationFrame(() => assignSrc());
			return;
		}
		this.isLoadingIframe = true;
		assignSrc();
	}

	private isStorylineResumeLikeValue(value: string): boolean {
		if (!value || typeof value !== 'string') {
			return false;
		}
		return (
			value.includes('^v_player') ||
			value.includes('~_player') ||
			value.includes('"slideId"') ||
			value.includes('"suspendData"') ||
			/PlayerMemory/i.test(value)
		);
	}

	onStoryIframeLoaded(): void {
		const iframe = document.querySelector('#para-lms-assessment-frame') as HTMLIFrameElement;

		if (iframe?.contentDocument) {
			this.storyBridge.injectInitialStyles(iframe.contentDocument);
		}

		this.isLoadingIframe = false;
		if (iframe) {
			this.attachStoryBridge(iframe);
			this.storyBridge.scheduleRefresh();
		}
	}

	closeDrawer(): void {
		if (!this.drawerOpen) {
			return;
		}
		if (this.scoreStorageService.count > 0) {
			this.promptUnsavedScoresDiscard('drawer');
			return;
		}
		this.drawerEntered = false;
		this.cdr.markForCheck();
		if (this.closeDrawerTimer != null) {
			clearTimeout(this.closeDrawerTimer);
		}
		this.closeDrawerTimer = setTimeout(() => {
			this.closeDrawerImmediate();
			this.closeDrawerTimer = undefined;
		}, 320);
	}

	confirmCloseDrawer(): void {
		const intent = this.confirmCloseIntent;
		this.confirmCloseIntent = null;
		this.scoreStorageService.clearData();
		if (intent === 'page-refresh') {
			const finishReload = this.reloadNavigationFinish;
			this.clearPendingReloadNavigation();
			if (finishReload) {
				finishReload();
				return;
			}
			window.location.reload();
			return;
		}
		this.clearPendingReloadNavigation();
		this.closeDrawerImmediate();
	}

	cancelCloseDrawer(): void {
		const cancelReload = this.reloadNavigationCancel;
		this.confirmCloseIntent = null;
		this.clearPendingReloadNavigation();
		if (cancelReload) {
			cancelReload();
		}
		this.drawerState = 'assessing';
		this.cdr.markForCheck();
	}

	private closeDrawerImmediate(): void {
		if (this.closeDrawerTimer != null) {
			clearTimeout(this.closeDrawerTimer);
			this.closeDrawerTimer = undefined;
		}
		this.assessmentContextNodeId = null;
		this.lmsAssessmentContext = null;
		this.activeLmsHeaderId = null;
		this.lmsSlideGuideVisible = false;
		this.lastLmsSlideGuideKey = null;
		this.lmsStableSlideGuideLines = [];
		if (this.lmsSlideGuideResizeTimer != null) {
			clearTimeout(this.lmsSlideGuideResizeTimer);
			this.lmsSlideGuideResizeTimer = undefined;
		}

		this.teardownStoryObservers();
		this.clearWindowStoryHooks();
		this.resumeStorageOverride.disable();
		this.confirmCloseIntent = null;
		this.clearPendingReloadNavigation();
		this.drawerState = 'closed';
		this.drawerEntered = false;
		this.lastStoryUrlPath = null;
		this.iframeUrl = null;
		this.activeStudent = null;
		this.cdr.markForCheck();
	}

	private setWindowStoryHooks(): void {
		(window as any).scoreFromStoryLine = this.scoreFromStoryLine.bind(this);
		(window as any).paraLmsSetActiveHeader = this.setActiveLmsHeaderFromStoryLine.bind(this);
	}

	private clearWindowStoryHooks(): void {
		if ((window as any).scoreFromStoryLine) {
			delete (window as any).scoreFromStoryLine;
		}
		if ((window as any).paraLmsSetActiveHeader) {
			delete (window as any).paraLmsSetActiveHeader;
		}
	}

	/**
	 * Called from Storyline when a scored slide is shown.
	 * Example trigger JS: `parent.paraLmsSetActiveHeader('slide2');`
	 */
	public setActiveLmsHeaderFromStoryLine(headerId: string): void {
		const normalized = normalizeLmsHeaderId(headerId);
		if (!normalized) {
			return;
		}
		this.ngZone.run(() => {
			this.activeLmsHeaderId = normalized;
			this.lmsStableSlideGuideLines = [];
			debugParaLms('paraLmsSetActiveHeader', { raw: headerId, normalized });
			this.storyBridge.scheduleRefresh();
			this.syncLmsSlideGuideVisibilityForActiveSlide();
			this.cdr.markForCheck();
		});
	}

	private teardownStoryObservers(): void {
		this.storyBridge.detach();
		this.storyVisibleScoreButtons = [];
		this.storyScoreButtonProbes = [];
		this.storyIframeViewWidth = 0;
		this.storyNextControlVisible = false;
	}

	private isStoryBridgeActive(): boolean {
		return this.drawerOpen && this.drawerState === 'assessing' && !this.isLocked && !this.isLoadingIframe;
	}

	private attachStoryBridge(iframe: HTMLIFrameElement): void {
		this.storyBridge.attach(iframe, {
			isActive: () => this.isStoryBridgeActive(),
			onScoreClick: (score) => this.scoreFromStoryLine(score),
			onLayoutChange: (snapshot) => this.applyStoryLayoutSnapshot(snapshot),
			shouldInterceptPrev: () => this.canGoToPreviousChallenge() && this.isOnFirstSlideOfChallenge(),
			onPrevCrossChallenge: () => this.goToPreviousChallenge(),
			canGoToPreviousChallenge: () => this.canGoToPreviousChallenge(),
			debugPrev: (context, extra) => this.logPrevCrossChallengeDebug(context, extra),
		});
	}

	private applyStoryLayoutSnapshot(snapshot: LmsStoryLayoutSnapshot): void {
		this.storyScoreButtonProbes = snapshot.scoreButtonProbes;
		this.storyIframeViewWidth = snapshot.viewWidth;
		this.storyVisibleScoreButtons = snapshot.visibleScoreButtons;
		this.storyNextControlVisible = snapshot.nextControlVisible;
		this.syncLmsSlideGuideVisibilityForActiveSlide();
		if (this.lmsSlideGuideVisible) {
			this.updateLmsSlideGuidePanelPosition();
		}
		this.cdr.markForCheck();
	}

	private logPrevCrossChallengeDebug(context: string, extra?: Record<string, unknown>): void {
		const rawQ = this.getCurrentChallenge();
		const q = normalizeNextChallengeForLms(rawQ, this.questions) ?? rawQ;
		debugParaLms(`PREV cross-challenge: ${context}`, {
			drawerState: this.drawerState,
			isLocked: this.isLocked,
			canGoToPreviousChallenge: this.canGoToPreviousChallenge(),
			hasPreviousCatalogChallenge: this.hasPreviousCatalogChallengeBeforeCurrent(),
			isOnFirstSlideOfChallenge: this.isOnFirstSlideOfChallenge(),
			activeLmsHeaderId: this.activeLmsHeaderId,
			currentChallengeId: q?.id,
			currentQuestionNumber: q?.questionNumber,
			...extra,
		});
	}

	public scoreFromStoryLine(score: number): void {
		if (this.isLocked) return;
		this.confirmScore = score;
		this.ngZone.run(() => this.persistCurrentScoreAndAdvance());
	}

	returnToScreener(): void {
		this.paraSelectedAssessment.select('para-ce-screener');
		this.closeDrawer();
	}

	goBackFromConfirmation(): void {
		this.drawerState = 'assessing';
		this.cdr.markForCheck();
	}

	goToConfirmation(): void {
		this.drawerState = 'confirmation';
		this.cdr.markForCheck();
	}

	toggleDateSelector(): void {
		this.showDateSelector = !this.showDateSelector;
	}

	deleteScore(storageKey: string): void {
		this.scoreStorageService.removeScore(storageKey);
		if (this.scoreStorageService.count === 0) {
			this.drawerState = 'assessing';
		}
		this.cdr.markForCheck();
	}

	storeScore(): void {
		this.persistCurrentScoreAndAdvance();
	}

	/** Save the current Storyline score, load the next challenge, or submit when the catalog is finished. */
	private persistCurrentScoreAndAdvance(): void {
		const rawQ = this.getCurrentChallenge();
		const q = normalizeNextChallengeForLms(rawQ, this.questions) ?? rawQ;
		const payload = {
			participantId: this.activeStudent.id,
			testSystemId: 1,
			assessmentBookId: q?.assessmentBookId ?? this.activeStudent.assessmentBookId,
			assessmentTemplateId: this.activeStudent.assessmentTemplateId,
			question: q,
			score: this.confirmScore,
			date: this.selectedDate,
		};

		const nextQuestion = getNextChallengeQuestionAfter(
			q,
			this.questions,
			this.activeStudent?.assessmentBookId,
			this.activeStudent?.assessmentTemplateId
		);
		if (nextQuestion) {
			this.activeStudent.challange.nextChallenge = nextQuestion;
		} else if (this.activeStudent.challange) {
			this.activeStudent.challange.nextChallenge = null;
		}
		this.scoreStorageService.setData(payload);

		if (nextQuestion) {
			this.drawerState = 'assessing';
			this.loadStoryIframe(this.activeStudent);
			this.cdr.markForCheck();
			return;
		}

		this.drawerState = 'assessing';
		if (this.isScreenerLmsAssessment()) {
			this.goToConfirmation();
			return;
		}

		this.cdr.markForCheck();
	}

	setConfirm(): void {
		const rawQ = this.getCurrentChallenge();
		const q = normalizeNextChallengeForLms(rawQ, this.questions) ?? rawQ;
		const payload = {
			participantId: this.activeStudent.id,
			testSystemId: 1,
			assessmentBookId: q?.assessmentBookId ?? this.activeStudent.assessmentBookId,
			assessmentTemplateId: this.activeStudent.assessmentTemplateId,
			question: q,
			score: this.confirmScore,
			date: this.selectedDate,
		};

		this.scoreStorageService.setData(payload);
		this.drawerState = 'confirmation';
		this.cdr.markForCheck();
	}

	submitScore(): void {
		if (this.isSubmitting) return;
		const rawAssessmentId = this.assessmentId ?? this.currentAssessmentId();
		const assessmentId = rawAssessmentId != null ? String(rawAssessmentId).trim() : '';
		if (!assessmentId) {
			console.error('[PARA LMS] Cannot submit scores: missing assessmentId (select an assessment or reopen the drawer).');
			return;
		}
		this.isSubmitting = true;
		this.drawerState = 'submitting';
		this.cdr.markForCheck();
		const nodeId = this.assessmentContextNodeId ?? normalizeNodeId(this.paraOrgState.getSelectedClassroomSnapshot()?.id);
		this.paraScoreSubmit
			.submitScores({
				scores: this.scoreStorageService.storedScores,
				nodeId,
				assessmentId,
			})
			.subscribe({
				next: (response) => {
					const reportNodeId = this.assessmentContextNodeId ?? normalizeNodeId(this.paraOrgState.getSelectedClassroomSnapshot()?.id);
					if (reportNodeId != null) {
						this.paraProgressReport.refreshProgressReport(reportNodeId);
						// Refresh assessment series (unlock state can change after submit).
						this.paraSeries.refreshAssessmentSeries(reportNodeId);
					}
					// Enrich challenge payload so badge/context can update immediately.
					// Server returns highestChallenge without phase, but currentChallenge/nextChallenge include phase metadata.
					const challenges = response?.challenges;
					if (challenges && typeof challenges === 'object') {
						const hi = challenges.highestChallenge;
						const cur = challenges.currentChallenge;
						if (hi && typeof hi === 'object' && (hi as any).phase == null && cur && typeof cur === 'object') {
							// If currentChallenge matches the submitted/highest question, copy its phase onto highestChallenge.
							const hiQid = Number((hi as any).questionId);
							const curId = Number((cur as any).id);
							if (Number.isFinite(hiQid) && Number.isFinite(curId) && hiQid === curId) {
								(hi as any).phase = (cur as any).phase ?? null;
							}
						}
						// Keep active student + cached roster in sync for immediate UI updates.
						if (this.activeStudent) {
							this.activeStudent.challange = { ...(this.activeStudent.challange || {}), ...challenges };
						}
						if (this.students?.length && this.activeStudent?.id != null) {
							const updatedStudent = this.students.find((s) => String(s.id) === String(this.activeStudent.id));
							if (updatedStudent) {
								updatedStudent.challange = { ...(updatedStudent.challange || {}), ...challenges };
							}
						}
						// Update drawer context (so badge colors + highestChallenge are always "on hand").
						if (this.lmsContext) {
							this.lmsContext = { ...this.lmsContext, highestChallenge: hi ?? this.lmsContext.highestChallenge };
						}
					}
					this.isSubmitting = false;
					const updatedStudent = this.students?.find((student) => student.id === this.activeStudent.id);
					if (updatedStudent && response?.challenges) {
						const element = document.querySelector(`[data-student-id="${updatedStudent.id}"]`);
						if (element) {
							element.classList.add('number-up-animation');
							setTimeout(() => element.classList.remove('number-up-animation'), 1000);
						}
					}

					const finishAfterSubmit = () => {
						this.scoreStorageService.clearData();
						this.drawerState = 'assessing';
						this.closeDrawer();
					};

					this.drawerState = 'done';
					this.cdr.markForCheck();
					setTimeout(() => {
						finishAfterSubmit();
					}, 500);
				},
				error: (err: any) => {
					// Prefer a helpful server message (e.g., "No valid scores to submit.")
					const msg = err?.error?.message ?? err?.error ?? err?.message ?? 'Submit failed. Fix any issues and try again.';
					console.error('[PARA LMS] Submit failed.', msg, err);
					this.isSubmitting = false;
					this.drawerState = 'confirmation';
					this.cdr.markForCheck();
				},
			});
	}
}
